import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import {
  generateBillingInvoicePDF,
  downloadInvoicePDF,
  createPDFPreviewURL,
  BillingInvoiceData,
  BillingInvoiceItem,
} from '../../utils/billing-invoice-pdf';

// ========================================
// 型定義
// ========================================
interface InvoiceItem {
  id?: string;
  constructionId?: string | null;
  productIndex?: number | null;
  constructionDate: string;
  companyName: string;
  productName: string;
  model: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  displayOrder: number;
}

interface Partner {
  id: string;
  name: string;
  code: string;
  email: string | null;
  contactEmail: string | null;
  phone: string | null;
  address: string | null;
  postalCode: string | null;
}

interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  constructionPartnerId: string | null;
  partnerName: string | null;
  invoiceDate: string;
  dueDate: string | null;
  subtotal: string | null;
  taxRate: string | null;
  taxAmount: string | null;
  totalAmount: string | null;
  status: string;
  paymentDate: string | null;
  notes: string | null;
  items: {
    id: string;
    constructionId: string | null;
    productIndex: number | null;
    constructionDate: string | null;
    companyName: string | null;
    productName: string;
    model: string | null;
    quantity: string;
    unitPrice: string;
    amount: string;
    displayOrder: number;
  }[];
  partner: Partner | null;
}

const statusOptions = [
  { value: 'draft', label: '下書き' },
  { value: 'sent', label: '送信済' },
  { value: 'paid', label: '入金済' },
  { value: 'cancelled', label: 'キャンセル' },
];

const statusLabels: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'danger' }> = {
  draft: { label: '下書き', variant: 'default' },
  sent: { label: '送信済', variant: 'info' },
  paid: { label: '入金済', variant: 'success' },
  cancelled: { label: 'キャンセル', variant: 'danger' },
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
};

const formatDateForInput = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  if (dateStr.includes('T')) return dateStr.split('T')[0];
  return dateStr;
};

// ========================================
// メインコンポーネント
// ========================================
export default function BillingInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 状態
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);

  // 編集フォーム
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState('draft');
  const [paymentDate, setPaymentDate] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [constructionPartnerId, setConstructionPartnerId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([]);

  // パートナー一覧
  const [partners, setPartners] = useState<Partner[]>([]);

  // PDF
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);

  // メールモーダル
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // ========================================
  // データ取得
  // ========================================
  const fetchInvoice = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.get<InvoiceDetail>(`/api/billing-invoices/${id}`);
      setInvoice(data);

      // フォームに反映
      setInvoiceNumber(data.invoiceNumber);
      setInvoiceDate(formatDateForInput(data.invoiceDate));
      setDueDate(formatDateForInput(data.dueDate));
      setStatus(data.status);
      setPaymentDate(formatDateForInput(data.paymentDate));
      setPartnerName(data.partnerName || '');
      setConstructionPartnerId(data.constructionPartnerId || '');
      setNotes(data.notes || '');

      // 内訳をフォーム用に変換
      setItems(
        data.items.map(item => ({
          id: item.id,
          constructionId: item.constructionId,
          productIndex: item.productIndex,
          constructionDate: formatDateForInput(item.constructionDate) || '',
          companyName: item.companyName || '',
          productName: item.productName,
          model: item.model || '',
          quantity: parseFloat(item.quantity) || 1,
          unitPrice: parseFloat(item.unitPrice) || 0,
          amount: parseFloat(item.amount) || 0,
          displayOrder: item.displayOrder,
        }))
      );

      // メール情報
      if (data.partner) {
        setEmailTo(data.partner.contactEmail || data.partner.email || '');
      }
      setEmailSubject(`【請求書送付】${data.invoiceNumber} - ${data.partnerName || ''}`);
      setEmailMessage(
        `平素より大変お世話になっております。\n\n下記の通り請求書を送付いたしますので、ご査収くださいますようお願い申し上げます。\n\nご不明な点がございましたら、お気軽にお問い合わせください。\n\n何卒よろしくお願い申し上げます。`
      );
    } catch (error) {
      console.error('Failed to fetch billing invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const data = await api.get<{ items: Partner[] }>('/api/billing-invoices/partners/list');
      setPartners(data.items || []);
    } catch {
      setPartners([]);
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchPartners();
  }, [id]);

  // ========================================
  // 金額計算
  // ========================================
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = Math.floor(subtotal * 0.1);
  const totalAmount = subtotal + taxAmount;

  // ========================================
  // 製品操作
  // ========================================
  // データ変更時にPDFキャッシュをクリア
  const clearPDFCache = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPdfBase64(null);
  };

  const handleAddItem = () => {
    clearPDFCache();
    setItems([
      ...items,
      {
        constructionDate: '',
        companyName: '',
        productName: '',
        model: '',
        quantity: 1,
        unitPrice: 0,
        amount: 0,
        displayOrder: items.length,
      },
    ]);
  };

  const handleUpdateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    clearPDFCache();
    const newItems = [...items];
    const item = { ...newItems[index], [field]: value };

    // 金額を自動計算
    if (field === 'quantity' || field === 'unitPrice') {
      item.amount = (item.quantity || 0) * (item.unitPrice || 0);
    }

    newItems[index] = item;
    setItems(newItems);
  };

  const handleDeleteItem = (index: number) => {
    clearPDFCache();
    setItems(items.filter((_, i) => i !== index));
  };

  // ========================================
  // 保存
  // ========================================
  const handleSave = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await api.put(`/api/billing-invoices/${id}`, {
        invoiceNumber,
        invoiceDate,
        dueDate: dueDate || undefined,
        status,
        paymentDate: paymentDate || undefined,
        constructionPartnerId: constructionPartnerId || undefined,
        partnerName,
        notes,
        items: items.map((item, index) => ({
          constructionId: item.constructionId || undefined,
          productIndex: item.productIndex ?? undefined,
          constructionDate: item.constructionDate || undefined,
          companyName: item.companyName || undefined,
          productName: item.productName,
          model: item.model || undefined,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          amount: item.amount,
          displayOrder: index,
        })),
      });
      fetchInvoice();
    } catch (error) {
      console.error('Failed to save billing invoice:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // ========================================
  // PDF生成
  // ========================================
  const createPDFData = (): BillingInvoiceData => {
    const pdfItems: BillingInvoiceItem[] = items.map(item => ({
      constructionDate: item.constructionDate,
      companyName: item.companyName,
      productName: item.productName,
      amount: item.amount,
    }));

    return {
      invoiceNumber,
      invoiceDate,
      dueDate,
      partnerName: partnerName || '御中',
      items: pdfItems,
      subtotal,
      taxRate: 10,
      taxAmount,
      totalAmount,
      notes: notes || undefined,
    };
  };

  const handlePreview = async () => {
    setGenerating(true);
    try {
      const data = createPDFData();
      const { blob, base64 } = await generateBillingInvoicePDF(data);
      const url = createPDFPreviewURL(blob);
      setPreviewUrl(url);
      setPdfBase64(base64);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      toast.error('PDFの生成に失敗しました');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const data = createPDFData();
      const { blob } = await generateBillingInvoicePDF(data);
      downloadInvoicePDF(blob, `請求書_${invoiceNumber}.pdf`);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      toast.error('PDFの生成に失敗しました');
    } finally {
      setGenerating(false);
    }
  };

  // ========================================
  // メール送信
  // ========================================
  const handleOpenEmailModal = async () => {
    if (!pdfBase64) {
      setGenerating(true);
      try {
        const data = createPDFData();
        const { base64 } = await generateBillingInvoicePDF(data);
        setPdfBase64(base64);
      } catch (error) {
        console.error('PDF生成エラー:', error);
        toast.error('PDFの生成に失敗しました');
        setGenerating(false);
        return;
      }
      setGenerating(false);
    }
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!id || !pdfBase64) return;
    if (!emailTo) {
      setEmailError('送信先メールアドレスを入力してください');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailTo)) {
      setEmailError('有効なメールアドレスを入力してください');
      return;
    }

    setEmailSending(true);
    setEmailError(null);
    try {
      await api.post(`/api/billing-invoices/${id}/send-email`, {
        to: emailTo,
        subject: emailSubject,
        message: emailMessage,
        pdfBase64,
      });
      setShowEmailModal(false);
      setStatus('sent');
      fetchInvoice();
    } catch (error) {
      console.error('メール送信エラー:', error);
      setEmailError('メールの送信に失敗しました。もう一度お試しください。');
    } finally {
      setEmailSending(false);
    }
  };

  // ========================================
  // パートナー変更
  // ========================================
  const handlePartnerChange = (partnerId: string) => {
    clearPDFCache();
    setConstructionPartnerId(partnerId);
    if (partnerId) {
      const partner = partners.find(p => p.id === partnerId);
      if (partner) {
        setPartnerName(partner.name);
        setEmailTo(partner.contactEmail || partner.email || '');
      }
    }
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">読み込み中...</div>;
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">請求書が見つかりません</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/billing-invoices')}>
          一覧に戻る
        </Button>
      </div>
    );
  }

  const statusInfo = statusLabels[status] || { label: status, variant: 'default' as const };
  const isReadOnly = status === 'sent' || status === 'paid';

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/billing-invoices')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            請求書 {invoiceNumber}
          </h1>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
        </div>
      </div>

      {/* 基本情報 */}
      <Card title="基本情報">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="請求書番号"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            disabled={isReadOnly}
          />
          <Input
            label="請求日"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            disabled={isReadOnly}
          />
          <Input
            label="支払期限"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={isReadOnly}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Select
            label="施工パートナー"
            value={constructionPartnerId}
            onChange={(e) => handlePartnerChange(e.target.value)}
            disabled={isReadOnly}
          >
            <option value="">選択してください</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <Select
            label="ステータス"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          {status === 'paid' && (
            <Input
              label="入金日"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          )}
        </div>
      </Card>

      {/* 請求内訳 */}
      <Card title="請求内訳">
        <div className="space-y-3">
          {/* テーブルヘッダー */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-28">施工日</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">企業名</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">商材</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-28">金額</th>
                  {!isReadOnly && (
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-16">操作</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={item.constructionDate}
                        onChange={(e) => handleUpdateItem(index, 'constructionDate', e.target.value)}
                        disabled={isReadOnly}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.companyName}
                        onChange={(e) => handleUpdateItem(index, 'companyName', e.target.value)}
                        disabled={isReadOnly}
                        placeholder="企業名"
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.productName}
                        onChange={(e) => handleUpdateItem(index, 'productName', e.target.value)}
                        disabled={isReadOnly}
                        placeholder="商材名"
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.amount}
                        onChange={(e) => {
                          const amt = parseFloat(e.target.value) || 0;
                          clearPDFCache();
                          const newItems = [...items];
                          newItems[index] = {
                            ...newItems[index],
                            amount: amt,
                            unitPrice: amt,
                            quantity: 1,
                          };
                          setItems(newItems);
                        }}
                        disabled={isReadOnly}
                        className="w-full px-2 py-1 text-sm text-right border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </td>
                    {!isReadOnly && (
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleDeleteItem(index)}
                          className="text-red-500 hover:text-red-700"
                          title="削除"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {items.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p>内訳がありません</p>
            </div>
          )}

          {!isReadOnly && (
            <Button variant="secondary" size="sm" onClick={handleAddItem}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              行を追加
            </Button>
          )}
        </div>
      </Card>

      {/* 合計金額 */}
      <Card>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">小計（税抜）</span>
              <span className="text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">消費税（10%）</span>
              <span className="text-gray-900 dark:text-white">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="border-t border-blue-200 dark:border-blue-700 pt-2 flex justify-between">
              <span className="font-medium text-gray-900 dark:text-white">合計（税込）</span>
              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* 備考 */}
      <Card title="備考">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={isReadOnly}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="備考を入力..."
        />
      </Card>

      {/* PDFプレビュー */}
      {previewUrl && (
        <Card>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PDFプレビュー</span>
              <button
                onClick={() => setPreviewUrl(null)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src={previewUrl}
              className="w-full max-h-[50vh] min-h-[300px]"
              title="請求書プレビュー"
            />
          </div>
        </Card>
      )}

      {/* アクションボタン */}
      <div className="flex flex-wrap gap-3 pb-8">
        {!isReadOnly && (
          <Button onClick={handleSave} loading={saving}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            保存
          </Button>
        )}
        <Button variant="secondary" onClick={handlePreview} loading={generating}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          プレビュー
        </Button>
        <Button variant="secondary" onClick={handleDownload} loading={generating}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          ダウンロード
        </Button>
        <Button variant="primary" onClick={handleOpenEmailModal} loading={generating}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          メール送信
        </Button>
      </div>

      {/* メール送信モーダル */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="請求書メール送信"
        size="wide"
      >
        <div className="space-y-4">
          {/* 送信情報サマリー */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium text-blue-900 dark:text-blue-100">送信する請求書</span>
            </div>
            <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <p>請求書番号: {invoiceNumber}</p>
              <p>請求先: {partnerName || '(未設定)'}</p>
              <p>請求金額: <span className="font-bold">{formatCurrency(totalAmount)}</span></p>
            </div>
          </div>

          {emailError && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
              {emailError}
            </div>
          )}

          <Input
            label="送信先メールアドレス"
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="example@company.co.jp"
            required
          />
          <Input
            label="件名"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              メッセージ本文
            </label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>請求書PDFがメールに添付されます。送信後、ステータスが「送信済」に更新されます。</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={() => setShowEmailModal(false)} disabled={emailSending}>
              キャンセル
            </Button>
            <Button onClick={handleSendEmail} loading={emailSending}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              送信
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
